// What a chart's Custom range is allowed to be, on the client side (#184).
//
// The mirror of DataIngestionESM/lib/chartRange.mjs. Checked here as well as
// there so an out-of-range period is refused beside the field that set it,
// rather than after a round trip the operator would read as "the chart failed" —
// the same argument reportDownload.js's periodError() makes for the report.
//
// Before this, `days > 183` was written out separately in TowerDriftChart,
// PowerMeterChart, TemperatureChart and WeatherStationChart. Four copies of one
// rule on this side, four more on the other, and nothing connecting any of them.
//
// Deliberately NOT shared with reportDownload.js's MAX_PERIOD_DAYS, which stays
// at six months. A chart is a downsampled read; the corridor report assembles
// per-device sections and renders a PDF, at a cost that grows with the period.
// #184 raised one and left the other alone on purpose.

/**
 * The earliest date any chart will accept, as `YYYY-MM-DD`.
 *
 * Must equal CHART_RANGE_FLOOR in DataIngestionESM/lib/chartRange.mjs. It is a
 * date-only string because that is what an `<input type="date">` min attribute
 * takes, which is the point: the picker cannot offer what the API will refuse.
 *
 * Sixteen days before the oldest reading in the system (2026-01-17T16:12:32), so
 * it is a round date nobody has to look up and it cannot exclude a reading.
 */
export const CHART_RANGE_FLOOR = '2026-01-01';

const DAY_MS = 86_400_000;

/**
 * The preset ranges every chart offers, in the order they are rendered (#231).
 *
 * Defined once rather than in each of the four chart components, for the reason
 * #184 shared the floor: the same list written out four times is a list that
 * ends up different in four places. `key` is a translation key, not a label, so
 * each caller renders it in the operator's language.
 *
 * The six-month entry is a CONVENIENCE WINDOW, not a cap. #184 deliberately
 * removed the six-month ceiling, and nothing here may read as if it had come
 * back: it sits in the list as one choice among four, with no maximum implied.
 *
 * Its label says months and its value says days on purpose. `parseRange` in
 * routes/station.mjs understands an `h`/`d` grammar only, so `180d` is a range
 * the API already speaks and this needs no backend change. A real `m` unit
 * would additionally make it a true calendar six months rather than a flat 180
 * days, which is more correct but is a change to four routes for the sake of a
 * label. If that is ever wanted it is its own issue, and this note is why the
 * two are not the same change.
 */
export const RANGE_PRESETS = [
  { key: 'range24h', value: '24h' },
  { key: 'range7d', value: '7d' },
  { key: 'range30d', value: '30d' },
  { key: 'range6m', value: '180d' },
];

/**
 * The translation key for why this range cannot be charted, or null when it can.
 *
 * A key rather than a sentence, so the caller renders it in the operator's
 * language — same contract as reportDownload.js's periodError().
 *
 * There is no maximum span. Any period from the floor to today is valid.
 */
export function customRangeError(from, to) {
  if (!from || !to) return 'enterBothDates';
  if (from < CHART_RANGE_FLOOR) return 'minRange';
  const days = (new Date(to) - new Date(from)) / DAY_MS;
  if (!Number.isFinite(days)) return 'enterBothDates';
  if (days < 1) return 'endAfterStart';
  return null;
}

/**
 * The `YYYY-MM-DD` bounds of whatever range is currently selected.
 *
 * Needed because the pre-data notice below is not a custom-range concern. Three
 * of the devices came online in October, so a plain 30-day preset already opens
 * before their first reading, and a notice that only ran for Custom would miss
 * the case an operator hits by default.
 *
 * `range` is one of the presets ("24h", "7d", "30d") or "custom". Returns nulls
 * for a custom range that has not been applied yet, and for a preset it cannot
 * parse, so callers get no notice rather than a wrong one.
 */
export function windowBounds(range, committed = {}, now = new Date()) {
  if (range === 'custom') {
    return { from: committed.from || null, to: committed.to || null };
  }
  const match = /^(\d+)([hd])$/.exec(range ?? '');
  if (!match) return { from: null, to: null };
  const span = Number(match[1]) * (match[2] === 'h' ? 3_600_000 : DAY_MS);
  return {
    from: new Date(now.getTime() - span).toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

/**
 * What the chart has to say about the stretch before this device existed, or
 * null when there is nothing to say.
 *
 * Returns `{ key, firstReading }` where `key` is a translation key:
 *
 *   `preDataNone`    the window ends before this device's first reading, so the
 *                    chart is empty for a reason that is not an outage
 *   `preDataPartial` the window opens before the first reading, so the series
 *                    begins partway across the chart
 *
 * This exists because the floor is deliberately before any data. Four of the ten
 * devices in the deployment came online months after January — the weather
 * station on 2026-07-30, the energy meter on 2026-08-18, three more in October —
 * and a full-range chart draws their pre-installation months as a blank stretch.
 * A blank stretch is what an outage looks like. #155 exists because this product
 * refuses to let silence read as health, and this is that same error inverted:
 * without a caption, a device that did not yet exist reads as a device that went
 * quiet.
 *
 * `firstReading` null means "no readings at all", which is a different statement
 * from "no readings before X" and is deliberately not captioned here — the
 * chart's existing empty state already says it.
 */
export function preDataNotice(firstReading, windowStart, windowEnd) {
  if (!firstReading || !windowStart) return null;
  const firstMs = Date.parse(firstReading);
  const startMs = Date.parse(`${windowStart}T00:00:00.000Z`);
  if (!Number.isFinite(firstMs) || !Number.isFinite(startMs)) return null;

  // The window begins at or after the first reading: nothing is missing from the
  // front of it and there is nothing to explain.
  if (startMs >= firstMs) return null;

  // The end bound is inclusive of the whole day it names, matching the API.
  const endMs = windowEnd
    ? Date.parse(`${windowEnd}T23:59:59.999Z`)
    : Number.POSITIVE_INFINITY;
  if (!Number.isFinite(endMs) && windowEnd) return null;

  return {
    key: endMs < firstMs ? 'preDataNone' : 'preDataPartial',
    firstReading,
  };
}

/**
 * `preDataNotice`'s result as a rendered sentence, or null.
 *
 * The date is formatted to the day rather than the second: the operator is being
 * told when a device was installed, and a timestamp to the millisecond implies
 * the boundary matters more precisely than it does.
 */
export function preDataMessage(notice, t) {
  if (!notice) return null;
  const date = notice.firstReading.slice(0, 10);
  return t(notice.key, { date });
}
